import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IDatasource } from 'ag-grid-community';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
  formatDateRangeFilterLabel,
} from '../../components/shared/GridColumnFilterPanel';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import { Button } from '../../components/ui/button';
import { DataGrid, type GridApi } from '../../components/ui/table';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { fetchAuditLogGrid, fetchAuditLogList } from './api/api';
import {
  AUDIT_LOG_COLUMN_LABELS,
  buildAuditLogColumnFilterFields,
  createAuditLogColumnDefs,
} from './table/columnDefs';
import {
  AUDIT_LOG_GRID_DEFAULT_COL_DEF,
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  auditLogGridOptions,
} from './table/gridOptions';
import type {
  AuditLogColumnFilterKey,
  AuditLogColumnFilters,
  AuditLogRow,
} from './models/types';
import { EMPTY_AUDIT_LOG_COLUMN_FILTERS, SEARCH_DEBOUNCE_MS } from './constants/constants';
import {
  buildAuditLogRequest,
  getApiErrorMessage,
  mapAgGridFilterModelToAuditLogFilters,
} from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import './LogsAudit.css';

const LogsAudit: React.FC = () => {
  const [visiblePageRows, setVisiblePageRows] = useState<AuditLogRow[]>([]);
  const [statsRows, setStatsRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const statusTab = 'All';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<AuditLogColumnFilters>(
    EMPTY_AUDIT_LOG_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [tablePageSize, setTablePageSize] = useState(GRID_PAGE_SIZE);
  const gridApiRef = useRef<GridApi<AuditLogRow> | null>(null);
  const skipDatasourceResetRef = useRef(false);
  const tableFiltersRef = useRef({
    filterStatus: statusTab,
    search: debouncedSearch,
    columnFilters,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipDatasourceResetRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_AUDIT_LOG_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_AUDIT_LOG_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      skipDatasourceResetRef.current = true;
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback((values: Record<string, string[] | null>) => {
    const nextColumnFilters = {
      ...EMPTY_AUDIT_LOG_COLUMN_FILTERS,
      ...(Object.fromEntries(
        (Object.keys(EMPTY_AUDIT_LOG_COLUMN_FILTERS) as AuditLogColumnFilterKey[]).map(
          (key) => [key, values[key]?.length ? values[key] : null],
        ),
      ) as AuditLogColumnFilters),
    };
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: nextColumnFilters,
    };
    gridApiRef.current?.setFilterModel(null);
    setColumnFilters(nextColumnFilters);
  }, []);

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<AuditLogRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const handleTablePaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newSize = api.paginationGetPageSize();
    if (newSize && newSize !== tablePageSize) {
      setTablePageSize(newSize);
    }
  }, [tablePageSize]);

  const mergedColumnFilters = useMemo<AuditLogColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToAuditLogFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus: statusTab,
      search: debouncedSearch,
      columnFilters,
    };
  }, [statusTab, debouncedSearch, columnFilters]);

  const tableDatasource = useMemo<IDatasource>(
    () => ({
      getRows: async (params) => {
        const pageSize = params.endRow - params.startRow;
        const page = pageSize > 0 ? Math.floor(params.startRow / pageSize) : 0;
        setLoading(true);
        setLoadError(null);
        try {
          const rawFilterModel =
            (params.filterModel as Record<string, unknown> | null | undefined) ??
            (params.api?.getFilterModel() as Record<string, unknown> | null | undefined) ??
            null;
          const nativeColumnFilters = mapAgGridFilterModelToAuditLogFilters(rawFilterModel);
          const nextColumnFilters: AuditLogColumnFilters = {
            ...tableFiltersRef.current.columnFilters,
            ...nativeColumnFilters,
          };
          const { rows, totalElements } = await fetchAuditLogList(
            buildAuditLogRequest({
              filterStatus: tableFiltersRef.current.filterStatus,
              search: tableFiltersRef.current.search,
              columnFilters: nextColumnFilters,
              page,
              size: pageSize,
            }),
          );
          setVisiblePageRows(rows);
          params.successCallback(rows, totalElements);
        } catch (err: unknown) {
          setLoadError(getApiErrorMessage(err, 'Failed to load audit logs.'));
          setVisiblePageRows([]);
          params.failCallback();
        } finally {
          setLoading(false);
        }
      },
    }),
    [],
  );

  useEffect(() => {
    if (skipDatasourceResetRef.current) {
      skipDatasourceResetRef.current = false;
      return;
    }
    gridApiRef.current?.setGridOption('datasource', tableDatasource);
  }, [debouncedSearch, columnFilters, tableDatasource]);

  const fetchStatsRows = useCallback(async () => {
    try {
      const { rows } = await fetchAuditLogGrid({
        filterStatus: 'All',
        search: '',
        columnFilters: EMPTY_AUDIT_LOG_COLUMN_FILTERS,
      });
      setStatsRows(rows);
    } catch (err: unknown) {
      console.error('Failed to load audit log filter options:', err);
    }
  }, []);

  useAsyncEffect(async (isActive) => {
    if (!filterPanelOpen) return;
    await fetchStatsRows();
    if (!isActive()) return;
  }, [fetchStatsRows, filterPanelOpen]);

  const refreshRows = useCallback(() => {
    gridApiRef.current?.refreshInfiniteCache();
  }, []);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [AuditLogColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: AUDIT_LOG_COLUMN_LABELS[key],
        value:
          key === 'createdAt'
            ? formatDateRangeFilterLabel(value)
            : (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const sourceRows = statsRows.length > 0 ? statsRows : visiblePageRows;
    const fields = buildAuditLogColumnFilterFields(sourceRows);
    return fields.map((field) => {
      if (field.type === 'dateRange') return field;
      const applied = mergedColumnFilters[field.key as AuditLogColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [visiblePageRows, statsRows, mergedColumnFilters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToAuditLogFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      await downloadExcelExport({
        type: EXPORT_TYPES.AUDIT_LOGS,
        fileNamePrefix: 'audit-logs',
        errorMessage: 'Failed to export audit logs.',
        payload: buildAuditLogRequest({
          filterStatus: statusTab,
          search: debouncedSearch,
          columnFilters: {
            ...columnFilters,
            ...nativeColumnFilters,
          },
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  const columnDefs = useMemo(() => createAuditLogColumnDefs(), []);

  const gridOptions = useMemo(
    () => ({
      ...auditLogGridOptions,
      onPaginationChanged: handleTablePaginationChanged,
      onFilterChanged: handleGridFilterChanged,
    }),
    [handleTablePaginationChanged, handleGridFilterChanged],
  );

  return (
    <ListConsolePage
      title="Audit Logs"
      error={
        loadError ? (
          <span className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>{loadError}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tm-console-action-btn tm-console-action-btn--outline w-fit shrink-0"
              onClick={refreshRows}
            >
              Retry
            </Button>
          </span>
        ) : null
      }
    >
      <ListConsoleGridSection
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search user, action, entity…"
              disabled={loading}
              className="tm-toolbar-search"
              aria-label="Search audit logs"
            />
            <GridColumnFilterButton
              activeCount={activeColumnFilters.length}
              onClick={() => setFilterPanelOpen(true)}
              disabled={loading}
              className="tm-compact-icon-btn"
            />
            <GridExportButton
              onClick={() => void handleExport()}
              loading={exporting}
              disabled={loading || visiblePageRows.length === 0}
              className="tm-compact-icon-btn"
            />
          </div>
        }
        filterBar={
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
        }
      >
        <DataGrid<AuditLogRow>
          rowModelType="infinite"
          datasource={tableDatasource}
          cacheBlockSize={tablePageSize}
          columnDefs={columnDefs}
          includeAuditColumns={false}
          loading={loading}
          pagination
          paginationPageSize={tablePageSize}
          paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
          height="100%"
          className={LIST_CONSOLE_AG_GRID_CLASS}
          onGridReady={handleGridReady}
          defaultColDef={AUDIT_LOG_GRID_DEFAULT_COL_DEF}
          gridOptions={gridOptions}
        />
      </ListConsoleGridSection>

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />
    </ListConsolePage>
  );
};

export default LogsAudit;
